import { useEffect, useRef, useState } from "react";
import {
  Table,
  Button,
  Popconfirm,
  notification,
  Dropdown,
  Menu,
  Input,
  Space,
  Popover,
  Skeleton,
  Checkbox,
  Tag,
  Select,
} from "antd";
import {
  EditOutlined,
  SearchOutlined,
  DownloadOutlined,
  SettingOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import Highlighter from "react-highlight-words";
import { fetchCurrentUser, updateUser } from "../services/authAPI";
import { deleteContract, fetchContracts } from "../services/contractsAPI";
import NewOrUpdateContract from "../components/Contracts/NewOrUpdateContract";
import GenerateInvoiceModal from "../components/Contracts/GenerateInvoiceModal";
import { exportContractstoPDF } from "../utils/pdfUtils";
import { fetchVendors } from "../services/vendorsAPI";
import { getPreferredPageSize, savePreferredPageSize } from "../utils/paginationUtils";
import { formatDate } from "../utils/dateUtils";
import { getChangeOrderLabel, isTimeChangeOrdersEnabled, TIME_CONTRACT_COLUMNS, formatDurationDays, getTimeAwareColumnPreference, persistTimeAwareColumns } from "../utils/projectUtils";

const DEFAULT_CONTRACT_COLUMNS = [
  "number",
  "title",
  "contract_company",
  "status",
  "original_contract_amount",
  "pending_change_orders",
  "approved_change_orders",
  "revised_contract_amount",
  "total_change_orders",
  "action",
];

const getInitialContractColumns = (savedColumns) =>
  getTimeAwareColumnPreference({
    savedColumns,
    defaultColumns: DEFAULT_CONTRACT_COLUMNS,
    timeColumns: TIME_CONTRACT_COLUMNS,
  });

const Contracts = () => {
  const { selectedProject, currentUser, setCurrentUser } = useAuth();
  const projectId = selectedProject.project_id;
  const userRole = selectedProject.role;
  const isOwnerOrManager =
    userRole === "owner" || userRole === "project_manager";
  const coLabelPlural = getChangeOrderLabel(selectedProject, true);
  const timeChangeOrdersEnabled = isTimeChangeOrdersEnabled(selectedProject);
  const queryClient = useQueryClient();
  const [searchedColumns, setSearchedColumns] = useState("");
  const [filters, setFilters] = useState({});
  const [searchTexts, setSearchTexts] = useState({});
  const searchInput = useRef(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize('contracts'),
    total: 0,
  });
  const dt = useRef(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [isVisibleNewOrUpdateContract, setIsVisibleNewOrUpdateContract] =
    useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [invoiceContract, setInvoiceContract] = useState(null);
  const [defaultContractType, setDefaultContractType] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(() =>
    getInitialContractColumns(currentUser?.contract_columns),
  );

  const [isViewMode, setIsViewMode] = useState(false);
  const [tempSelection, setTempSelection] = useState(visibleColumns);
  const [tableKey, setTableKey] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const { data: contracts, isLoading } = useQuery({
    queryKey: [
      "contracts",
      projectId,
      pagination.current,
      pagination.pageSize,
      filters,
      sortField,
      sortOrder,
    ],
    queryFn: () =>
      fetchContracts(
        projectId,
        pagination.current,
        pagination.pageSize,
        filters,
        sortField,
        sortOrder
      ),
    enabled: !!projectId,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
      total: undefined,
    }));
  }, [filters]);

  useEffect(() => {
    const initialColumns = getInitialContractColumns(
      currentUser?.contract_columns,
    );
    setVisibleColumns(initialColumns);
    setTempSelection(initialColumns);
  }, [currentUser?.contract_columns, projectId]);

  const { data: projectVendors } = useQuery({
    queryKey: ["vendors", projectId, 1, 1000],
    queryFn: () => fetchVendors(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((acc, part) => acc && acc[part], obj);
  };

  const exportToCSV = (data, columns, filename = "export.csv") => {
    const headers = columns.map(
      (col) => col.csvDataIndex && (col.title || col.csvDataIndex)
    );
    const fields = columns.map((col) => col.csvDataIndex);

    const csvContent = [
      headers.join(","), // header
      ...data.map((row) =>
        fields
          .map((field, index) => {
            const value = field && getNestedValue(row, field);
            const cell = columns[index].csvRender
              ? columns[index].csvRender(value)
              : value;
            return typeof cell === "string" && cell.includes(",")
              ? `"${cell.replace(/"/g, '""')}"`
              : cell ?? ""; // null or undefined handling for empty cells
          })
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatContractRowForExport = (contract) => ({
    Number: contract.number,
    "Contract Company": contract.contract_company?.name,
    Title: contract.title,
    Status: contract.status || "N/A",
    "Start Date": formatDate(contract.contract_start_date),
    "Estimated Completion Date": formatDate(
      contract.contract_estimated_completion_date,
    ),
    "Original Contract Amount": formatCurrency(
      contract.original_contract_amount,
    ),
    [`Approved ${coLabelPlural}`]: formatCurrency(
      contract.approved_change_orders,
    ),
    [`Pending ${coLabelPlural}`]: formatCurrency(contract.pending_change_orders),
    "Revised Contract Amount": formatCurrency(
      contract.revised_contract_amount,
    ),
    ...(timeChangeOrdersEnabled
      ? {
          Duration: formatDurationDays(contract.duration),
          [`Approved ${coLabelPlural} (days)`]: formatDurationDays(
            contract.approved_change_order_days,
          ),
          "Revised Duration": formatDurationDays(contract.revised_duration),
        }
      : {}),
  });

  const fetchContractsForExport = async () => {
    const allContractsData = await fetchContracts(
      projectId,
      1,
      Math.max(contracts?.total || 0, 1),
      filters,
      sortField,
      sortOrder
    );
    return {
      exportOwnerContract: allContractsData?.owner_contract || null,
      exportVendorContracts: allContractsData?.contracts || [],
    };
  };

  const handleExportCSV = async () => {
    try {
      const { exportOwnerContract, exportVendorContracts } =
        await fetchContractsForExport();
      exportToCSV(
        [
          ...(exportOwnerContract ? [exportOwnerContract] : []),
          ...exportVendorContracts,
        ],
        columns,
        "contracts.csv"
      );
    } catch (error) {
      notification.error({
        message: "Failed to export CSV",
        description: error.message,
      });
    }
  };

  const handleExportPDF = async () => {
    try {
      const { exportOwnerContract, exportVendorContracts } =
        await fetchContractsForExport();
      const formattedData = [
        ...(exportOwnerContract
          ? [formatContractRowForExport(exportOwnerContract)]
          : []),
        ...exportVendorContracts.map(formatContractRowForExport),
      ];

      const title = exportOwnerContract
        ? "Contracts Report (Owner + Vendors; totals exclude owner)"
        : "Contracts Report";
      const fileName = "contracts-report.pdf";

      exportContractstoPDF(
        formattedData,
        title,
        fileName,
        contracts?.grand_total,
        coLabelPlural
      );
      notification.success({ message: "PDF exported successfully" });
    } catch (error) {
      notification.error({
        message: "Failed to export PDF",
        description: error.message,
      });
    }
  };

  const handleTableChange = (newPagination, _filters, sorter) => {
    const nextSortField = sorter?.order ? sorter.columnKey : null;
    const nextSortOrder =
      sorter?.order === "ascend" ? "asc" : sorter?.order === "descend" ? "desc" : null;
    const isSortChanged = nextSortField !== sortField || nextSortOrder !== sortOrder;
    setSortField(nextSortField);
    setSortOrder(nextSortOrder);
    setPagination(isSortChanged ? { ...newPagination, current: 1 } : newPagination);
    savePreferredPageSize(newPagination.pageSize, 'contracts');
  };

  const getColumnSortOrder = (key) =>
    sortField === key ? (sortOrder === "desc" ? "descend" : "ascend") : null;

  const columnSetMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: async (data, variables) => {
      const user = await fetchCurrentUser();
      localStorage.setItem("user", JSON.stringify(user));
      setCurrentUser(user);
      setIsPopoverOpen(false);
      const nextColumns = getInitialContractColumns(variables.contract_columns);
      setVisibleColumns(nextColumns);
      setTempSelection(nextColumns);
      setIsLoadingColumns(false);
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update columns",
        description: error.message,
      });
      setIsLoadingColumns(false);
    },
  });

  const handleSave = () => {
    setIsLoadingColumns(true);
    columnSetMutation.mutate({
      ...currentUser,
      contract_columns: persistTimeAwareColumns({
        selection: tempSelection,
        previousPreference: visibleColumns,
        enabled: timeChangeOrdersEnabled,
        timeColumns: TIME_CONTRACT_COLUMNS,
      }),
    });
  };

  const statuses = [
    { label: "Draft", value: "draft" },
    { label: "Out For Bid", value: "out_for_bid" },
    { label: "Out For Signature", value: "out_for_signature" },
    { label: "Approved", value: "approved" },
    { label: "Complete", value: "complete" },
    { label: "Terminated", value: "terminated" },
    { label: "Void", value: "void" },
    { label: "Executed", value: "executed" },
  ];

  function getStatusSeverity(status) {
    switch (status) {
      case "approved":
      case "complete":
        return "success";
      case "terminated":
      case "void":
        return "error";
      case "out_for_bid":
      case "out_for_signature":
        return "warning";
      case "draft":
        return "default";
      default:
        return "info";
    }
  }

  const handleCancel = () => {
    setTempSelection(visibleColumns);
    setIsPopoverOpen(false);
  };

  const deleteMutation = useMutation({
    mutationFn: ({ projectId, contractId }) =>
      deleteContract(projectId, contractId),
    onSuccess: () => {
      queryClient.invalidateQueries(["contracts", projectId]);
      notification.success({ message: "Contract deleted successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to delete Contract",
        description: error.message,
      });
    },
  });

  const ownerContract = contracts?.owner_contract || null;
  const vendorContracts = contracts?.contracts || [];

  const openCreateContract = (contractType = null) => {
    setIsViewMode(false);
    setSelectedContract(null);
    setDefaultContractType(contractType);
    setIsVisibleNewOrUpdateContract(true);
  };

  const openContractModal = (contract, viewMode = false) => {
    setSelectedContract(contract);
    setDefaultContractType(
      contract?.contract_type === "owner" ? "owner" : null,
    );
    setIsViewMode(viewMode);
    setIsVisibleNewOrUpdateContract(true);
  };

  const handleUpdateClick = (contract) => openContractModal(contract, false);
  const handleViewClick = (contract) => openContractModal(contract, true);

  const handleDeleteClick = (contractId) => {
    deleteMutation.mutate({ projectId, contractId });
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    if (!searchedColumns.includes(dataIndex)) {
      setSearchedColumns([...searchedColumns, dataIndex]);
    }
    setSearchTexts((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }));
    setFilters((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }));
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    setSearchedColumns((prev) => prev.filter((col) => col !== dataIndex));
    clearFilters();
    confirm();
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[dataIndex];
      return newFilters;
    });
    setSearchTexts((prev) => {
      const updated = { ...prev };
      delete updated[dataIndex];
      return updated;
    });
  };

  const getColumnSearchProps = (
    dataIndex,
    type = "text",
    selectOptions = []
  ) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <>
          {type === "select" ? (
            <Select
              showSearch
              allowClear
              placeholder={`Select ${dataIndex}`}
              value={selectedKeys[0]}
              onChange={(value) => {
                setSelectedKeys(value !== undefined ? [value] : []);
              }}
              style={{ marginBottom: 8, display: "block" }}
              options={selectOptions}
            />
          ) : (
            <Input
              ref={searchInput}
              placeholder={`Search ${dataIndex}`}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() =>
                handleSearch(selectedKeys, confirm, dataIndex)
              }
              style={{ marginBottom: 8, display: "block" }}
            />
          )}
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() =>
                clearFilters && handleReset(clearFilters, confirm, dataIndex)
              }
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const target = record[dataIndex];

      if (!target) return false;

      if (typeof target === "object" && target?.name) {
        return target.name === value;
      }

      if (type === "select") {
        return target === value;
      }

      return target
        .toString()
        .toLowerCase()
        .includes(value.toString().toLowerCase());
    },
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };

  const renderFooter = () => (
    <div className="flex justify-between bg-gray-50 px-8 py-4 border-t border-gray-200">
      <div className="flex flex-col text-center w-1/4">
        <span className="text-gray-600 text-sm mb-1">
          Original Contract Amount
        </span>
        <span className="font-semibold text-sm mb-1">TOTAL</span>
        <span className="text-primary text-xl font-bold">
          {formatCurrency(contracts?.grand_total?.original_contract_amount)}
        </span>
      </div>
      <div className="flex flex-col text-center w-1/4">
        <span className="text-gray-600 text-sm mb-1">
          Approved {coLabelPlural}
        </span>
        <span className="font-semibold text-sm mb-1">TOTAL</span>
        <span className="text-primary text-xl font-bold">
          {formatCurrency(contracts?.grand_total?.approved_change_orders)}
        </span>
      </div>
      <div className="flex flex-col text-center w-1/4">
        <span className="text-gray-600 text-sm mb-1">
          Pending {coLabelPlural}
        </span>
        <span className="font-semibold text-sm mb-1">TOTAL</span>
        <span className="text-primary text-xl font-bold">
          {formatCurrency(contracts?.grand_total?.pending_change_orders)}
        </span>
      </div>
      <div className="flex flex-col text-center w-1/4">
        <span className="text-gray-600 text-sm mb-1">Total {coLabelPlural}</span>
        <span className="font-semibold text-sm mb-1">TOTAL</span>
        <span className="text-primary text-xl font-bold">
          {formatCurrency(contracts?.grand_total?.total_change_orders)}
        </span>
      </div>
      <div className="flex flex-col text-center w-1/4">
        <span className="text-gray-600 text-sm mb-1">
          Revised Contract Amount
        </span>
        <span className="font-semibold text-sm mb-1">TOTAL</span>
        <span className="text-primary text-xl font-bold">
          {formatCurrency(contracts?.grand_total?.revised_contract_amount)}
        </span>
      </div>
    </div>
  );

  const columns = [
    {
      title: "Number",
      dataIndex: "number",
      key: "number",
      isVisible: visibleColumns.includes("number"),
      csvDataIndex: "number",
      sorter: true,
      sortOrder: getColumnSortOrder("number"),
      ...getColumnSearchProps("number"),
      render: (_, rowData) => (
        <span>
          {rowData.contract_type === "purchase_order" && (
            <Tag color="blue" style={{ fontSize: "0.75rem", marginRight: 4 }}>
              PO
            </Tag>
          )}
          {rowData.contract_type === "subcontract" && (
            <Tag color="orange" style={{ fontSize: "0.75rem", marginRight: 4 }}>
              SC
            </Tag>
          )}
          {rowData.contract_type === "owner" && (
            <Tag color="purple" style={{ fontSize: "0.75rem", marginRight: 4 }}>
              Owner
            </Tag>
          )}
          <span className="text-primary font-semibold">{rowData.number}</span>
        </span>
      ),
    },

    {
      title: "Contract Company",
      dataIndex: "contract_company",
      key: "contract_company",
      isVisible: visibleColumns.includes("contract_company"),
      sorter: true,
      sortOrder: getColumnSortOrder("contract_company"),
      ...getColumnSearchProps(
        "contract_company",
        "select",
        projectVendors?.vendors?.map((v) => ({ value: v.name, label: v.name }))
      ),
      render: (contract_company) => contract_company?.name || "-",
      csvDataIndex: "contract_company.name",
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      isVisible: visibleColumns.includes("title"),
      sorter: true,
      sortOrder: getColumnSortOrder("title"),
      ...getColumnSearchProps("title"),
      csvDataIndex: "title",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      isVisible: visibleColumns.includes("status"),
      csvDataIndex: "status",
      ...getColumnSearchProps("status", "select", statuses),
      render: (status) => {
        if (!status) return <span>-</span>;

        const statusItem = statuses.find((s) => s.value === status);
        const formattedStatus = statusItem ? statusItem.label : status;
        const severity = getStatusSeverity(status);

        const severityColorMap = {
          success: "green",
          warning: "orange",
          error: "red",
          info: "blue",
          default: "default",
        };

        return <Tag color={severityColorMap[severity]}>{formattedStatus}</Tag>;
      },
    },
    {
      title: "Start Date",
      dataIndex: "contract_start_date",
      key: "contract_start_date",
      csvDataIndex: "contract_start_date",
      csvRender: formatDate,
      isVisible: visibleColumns.includes("contract_start_date"),
      render: (contract_start_date) => formatDate(contract_start_date),
    },
    {
      title: "Estimated Completion Date",
      dataIndex: "contract_estimated_completion_date",
      key: "contract_estimated_completion_date",
      csvDataIndex: "contract_estimated_completion_date",
      csvRender: formatDate,
      isVisible: visibleColumns.includes("contract_estimated_completion_date"),
      render: (contract_estimated_completion_date) =>
        formatDate(contract_estimated_completion_date),
    },
    // {
    //   title: "Executed",
    //   dataIndex: "executed",
    //   key: "executed",
    //   csvDataIndex: "executed",
    //   isVisible: visibleColumns.includes("executed"),
    //   ...getColumnSearchProps("executed", "select", [
    //     { label: "Yes", value: true },
    //     { label: "No", value: false },
    //   ]),
    //   render: (executed) => {
    //     if (executed === null || executed === undefined) return <span>-</span>;
    //     return (
    //       <Tag color={executed ? "green" : "red"}>
    //         {executed ? "Yes" : "No"}
    //       </Tag>
    //     );
    //   },
    // },

    {
      title: "Original Contract Amount",
      dataIndex: "original_contract_amount",
      key: "original_contract_amount",
      csvDataIndex: "original_contract_amount",
      isVisible: visibleColumns.includes("original_contract_amount"),
      render: (original_contract_amount) =>
        formatCurrency(original_contract_amount),
    },

    {
      title: `Pending ${coLabelPlural}`,
      dataIndex: "pending_change_orders",
      key: "pending_change_orders",
      csvDataIndex: "pending_change_orders",
      isVisible: visibleColumns.includes("pending_change_orders"),
      render: (pending_change_orders) => formatCurrency(pending_change_orders),
    },
    {
      title: `Approved ${coLabelPlural}`,
      dataIndex: "approved_change_orders",
      key: "approved_change_orders",
      csvDataIndex: "approved_change_orders",
      isVisible: visibleColumns.includes("approved_change_orders"),
      render: (approved_change_orders) =>
        formatCurrency(approved_change_orders),
    },

    {
      title: "Revised Contract Amount",
      dataIndex: "revised_contract_amount",
      key: "revised_contract_amount",
      csvDataIndex: "revised_contract_amount",
      isVisible: visibleColumns.includes("revised_contract_amount"),
      render: (revised_contract_amount) =>
        formatCurrency(revised_contract_amount),
    },
    ...(timeChangeOrdersEnabled
      ? [
          {
            title: "Duration",
            dataIndex: "duration",
            key: "duration",
            csvDataIndex: "duration",
            isVisible: visibleColumns.includes("duration"),
            render: (duration) => formatDurationDays(duration),
          },
          {
            title: `Approved ${coLabelPlural} (days)`,
            dataIndex: "approved_change_order_days",
            key: "approved_change_order_days",
            csvDataIndex: "approved_change_order_days",
            isVisible: visibleColumns.includes("approved_change_order_days"),
            render: (days) => formatDurationDays(days),
          },
          {
            title: "Revised Duration",
            dataIndex: "revised_duration",
            key: "revised_duration",
            csvDataIndex: "revised_duration",
            isVisible: visibleColumns.includes("revised_duration"),
            render: (revised_duration) => formatDurationDays(revised_duration),
          },
        ]
      : []),
    {
      title: "Action",
      key: "action",
      isVisible: visibleColumns.includes("action"),
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              {isOwnerOrManager && (
                <>
                  {" "}
                  <Menu.Item key="1" onClick={() => handleViewClick(record)}>
                    <Button className="w-full">View</Button>
                  </Menu.Item>
                  <Menu.Item key="2" onClick={() => handleUpdateClick(record)}>
                    <Button className="w-full">Update</Button>
                  </Menu.Item>
                  <Menu.Item key="invoice" onClick={() => setInvoiceContract(record)}>
                    <Button className="w-full">Generate invoice</Button>
                  </Menu.Item>
                  <Menu.Item key="3">
                    <Popconfirm
                      title="Are you sure you want to delete this contract?"
                      onConfirm={() => handleDeleteClick(record?._id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button className="w-full" danger>
                        Delete
                      </Button>
                    </Popconfirm>
                  </Menu.Item>
                </>
              )}
            </Menu>
          }
          trigger={["click"]}
        >
          <Button icon={<EditOutlined />} size="medium"></Button>
        </Dropdown>
      ),
    },
  ];

  const content = (
    <div style={{ width: 200 }}>
      <Checkbox.Group
        style={{ display: "flex", flexDirection: "column", gap: 8 }}
        value={tempSelection}
        onChange={setTempSelection}
      >
        {columns.map((col) => (
          <Checkbox key={col.key} value={col.key}>
            {col.title}
          </Checkbox>
        ))}
      </Checkbox.Group>
      <div className="flex justify-end mt-3 gap-2">
        <Button size="small" onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="primary" size="small" onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );

  const handleClearAllFilters = () => {
    setFilters({});
    setSearchTexts({});
    setSearchedColumns([]);
    setSortField(null);
    setSortOrder(null);
    setTableKey((prev) => prev + 1);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Contracts</h1>
      <div className="flex gap-4 mb-4">
        {isOwnerOrManager && (
          <Button type="primary" onClick={() => openCreateContract()}>
            Create
          </Button>
        )}
        <Dropdown
          overlay={
            <Menu>
              {isOwnerOrManager && (
                <>
                  {" "}
                  <Menu.Item key="1" onClick={() => handleExportPDF()}>
                    <Button className="w-full">PDF</Button>
                  </Menu.Item>
                  <Menu.Item key="2" onClick={() => handleExportCSV()}>
                    <Button className="w-full">CSV</Button>
                  </Menu.Item>
                </>
              )}
            </Menu>
          }
          trigger={["click"]}
        >
          <Button icon={<DownloadOutlined />} size="medium">
            Export
          </Button>
        </Dropdown>
        <Popover
          content={isLoadingColumns ? <Skeleton active /> : content}
          title="Select Columns to Display"
          trigger="click"
          open={isPopoverOpen}
          onOpenChange={(visible) => setIsPopoverOpen(visible)}
        >
          <Button icon={<SettingOutlined />}>Columns</Button>
        </Popover>
        <Button
          icon={<ClearOutlined />}
          onClick={handleClearAllFilters}
          className="ml-auto"
        >
          Clear All Filters
        </Button>
      </div>

      {ownerContract && (
        <div className="mb-8 overflow-hidden rounded-md border border-gray-200">
          <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
            Contract with Owner
          </div>
          <Table
            columns={columns
              .filter((col) => col.isVisible === true)
              .map((col) => ({ ...col, sorter: false }))}
            dataSource={[ownerContract]}
            rowKey={(record) => record.id || record._id}
            pagination={false}
            showHeader
          />
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
          Contracts with Vendors
        </div>
        <Table
          ref={dt}
          footer={renderFooter}
          columns={columns.filter((col) => col.isVisible === true)}
          dataSource={vendorContracts}
          rowKey={(record) => record.id || record._id}
          key={tableKey}
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: contracts?.total,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
        />
      </div>
      {isVisibleNewOrUpdateContract && (
        <NewOrUpdateContract
          visible={isVisibleNewOrUpdateContract}
          onClose={() => {
            setIsVisibleNewOrUpdateContract(false);
            setDefaultContractType(null);
          }}
          contractId={selectedContract?._id}
          isViewMode={isViewMode}
          defaultContractType={defaultContractType}
          hasOwnerContract={!!ownerContract}
        />
      )}
      {invoiceContract && (
        <GenerateInvoiceModal
          key={`${projectId}-${invoiceContract._id || invoiceContract.id}`}
          contract={invoiceContract}
          project={selectedProject}
          onClose={() => setInvoiceContract(null)}
        />
      )}
    </div>
  );
};

export default Contracts;
